/**
 * The one-time code `k` the installer puts in the link's fragment (`/pair/<id>#k=<k>`).
 *
 * The fragment never reaches a server. On load the code is read once and the fragment is removed
 * from the address bar at once (not in history, bookmarks, a copied link or a sign-in return-to).
 * It is kept in this tab's sessionStorage, under a key bound to the request id, only while a
 * sign-in round trip may need it, and never past the request's expiry. It leaves the browser only
 * in the body of POST .../claim (or .../peek): never in a URL, a query string or a cookie.
 */
import { PAIR_TTL_MS, isPairFinal } from '@orgus/protocol';
import type { PairRequestView } from '@orgus/protocol';
import { claimPairRequest, peekPairRequest } from './api.ts';

const KEY = (id: string) => `orgus.pair.k.${id}`;
const K_RE = /(?:^#|&)k=([A-Za-z0-9_-]{16,64})(?:&|$)/;

interface Stored {
  k: string;
  /** Never kept past this (ms): the request's expiry, or 10 minutes from capture until it is known. */
  exp: number;
}

function read(id: string): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY(id));
    const v = raw ? (JSON.parse(raw) as Partial<Stored>) : null;
    return v && typeof v.k === 'string' && typeof v.exp === 'number' ? { k: v.k, exp: v.exp } : null;
  } catch {
    return null;
  }
}

function write(id: string, v: Stored): void {
  try {
    sessionStorage.setItem(KEY(id), JSON.stringify(v));
  } catch {
    /* storage off: the code still works in this page load */
  }
}

/** Take `k` from the address bar (and strip it), or from this tab's storage after a sign-in. */
export function capturePairCode(requestId: string, loc: Pick<Location, 'hash' | 'href'> = window.location, now: number = Date.now()): string | null {
  const m = K_RE.exec(loc.hash);
  if (m?.[1]) {
    write(requestId, { k: m[1], exp: now + PAIR_TTL_MS });
    const url = new URL(loc.href);
    url.hash = '';
    window.history.replaceState(window.history.state, '', url.toString());
    return m[1];
  }
  const stored = read(requestId);
  if (!stored) return null;
  if (now >= stored.exp) {
    forgetPairCode(requestId);
    return null;
  }
  return stored.k;
}

/** The request's real expiry is known: the code is not kept past it. */
export function limitPairCode(requestId: string, expiresAt: number): void {
  const stored = read(requestId);
  if (stored && expiresAt < stored.exp) write(requestId, { ...stored, exp: expiresAt });
}

/** The code was used, or the request ended: nothing keeps it. */
export function forgetPairCode(requestId: string): void {
  try {
    sessionStorage.removeItem(KEY(requestId));
  } catch {
    /* nothing to forget */
  }
}


const claims = new Map<string, Promise<PairRequestView>>();

/**
 * Claim a request with its code, once per page: a re-render, React's double effects in
 * development, or a second mount share the one call. The code is forgotten once it is used.
 */
export function claimOnce(requestId: string, k: string, claim: (id: string, body: { k: string }) => Promise<PairRequestView> = claimPairRequest): Promise<PairRequestView> {
  let p = claims.get(requestId);
  if (!p) {
    p = claim(requestId, { k }).then(
      (v) => {
        forgetPairCode(requestId);
        return v;
      },
      (e: unknown) => {
        // Refused (wrong code, ended, not allowed): the code is spent. A network failure keeps it for Try again.
        const status = (e as { status?: number } | null)?.status ?? 0;
        if (status >= 400 && status < 500) forgetPairCode(requestId);
        claims.delete(requestId);
        throw e;
      },
    );
    claims.set(requestId, p);
  }
  return p;
}

/**
 * PAIR-WEB-2: what the code from the link shows, without opening the request. A request nobody
 * opened yet waits for the person's click (`toOpen`); one already opened (by this session, on a
 * reload) is opened again at once, and one that ended is shown as it ended.
 */
export async function viewWithCode(
  requestId: string,
  k: string,
  peek: (id: string, k: string) => Promise<PairRequestView> = peekPairRequest,
  claim: (id: string, k: string) => Promise<PairRequestView> = (id, code) => claimOnce(id, code),
): Promise<{ view: PairRequestView; toOpen: boolean }> {
  const seen = await peek(requestId, k);
  if (seen.state === 'unclaimed') return { view: seen, toOpen: true };
  if (isPairFinal(seen.state)) return { view: seen, toOpen: false };
  return { view: await claim(requestId, k), toOpen: false };
}
